import React, { useMemo } from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router-dom";

import { Common } from "@src/styles/Common";
import { useHomePushStat } from "@src/hooks/queries/useHomePushStat";

/* 읽음률이 낮을 때 눈에 띄게 */
const GOOD = "#1BA345";
const WEAK = Common.colors.gray500;

const fmt = (n: number) => n.toLocaleString();

const fmtDate = (v: string | null) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    const p = (x: number) => String(x).padStart(2, "0");
    return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function AppPushStatBox() {
    const navigate = useNavigate();
    const { isLoading, isError, data } = useHomePushStat(8);

    const rows = data?.rows ?? [];

    const summary = useMemo(() => {
        const sent = rows.reduce((a, r) => a + r.messageCount, 0);
        const read = rows.reduce((a, r) => a + r.readCount, 0);
        return {
            isCollecting: rows.some((r) => r.isCollecting),
            sent,
            read,
            rate: sent > 0 ? (read / sent) * 100 : 0,
        };
    }, [rows]);

    return (
        <div css={cardStyle}>
            <div css={headRow}>
                <p css={titleP}>앱 푸시 통계</p>
                <span css={asOfSpan}>관리자가 보낸 전체 푸시 · 최근 {rows.length || 8}건 · 10초마다 갱신</span>
            </div>
            <p css={subP}>보낸 수는 발송 대상 회원 수입니다. 읽은 수는 푸시 또는 앱 알림함에서 내용을 연 사람 수입니다.</p>

            {isError ? (
                <div css={messageStyle}>데이터 조회에 실패하였습니다.</div>
            ) : (
                <>
                    <div css={summaryRow}>
                        <div css={summaryBox}>
                            <p className="label">최근 {rows.length || 8}건 보낸 수</p>
                            <p className="value">{isLoading ? "-" : summary.isCollecting ? "집계 중" : fmt(summary.sent)}</p>
                        </div>
                        <div css={summaryBox}>
                            <p className="label">읽은 수</p>
                            <p className="value">{isLoading ? "-" : summary.isCollecting ? "집계 중" : fmt(summary.read)}</p>
                        </div>
                        <div css={summaryBox}>
                            <p className="label">읽음률</p>
                            <p
                                className="value"
                                css={css`
                                    color: ${summary.rate >= 1 ? GOOD : WEAK};
                                `}
                            >
                                {isLoading ? "-" : summary.isCollecting ? "집계 중" : `${summary.rate.toFixed(2)}%`}
                            </p>
                        </div>
                        <div css={summaryBox}>
                            <p className="label">누적 발송 건수</p>
                            <p className="value">{isLoading ? "-" : fmt(data?.totalPushCount ?? 0)}</p>
                        </div>
                    </div>

                    <div css={tableWrap}>
                        <table css={tableStyle}>
                            <colgroup>
                                <col width="48%" />
                                <col width="14%" />
                                <col width="12%" />
                                <col width="10%" />
                                <col width="16%" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>제목</th>
                                    <th css={rightTh}>보낸 수</th>
                                    <th css={rightTh}>읽은 수</th>
                                    <th css={rightTh}>읽음률</th>
                                    <th>보낸 시각</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} css={emptyTd}>
                                            {isLoading ? "불러오는 중..." : "보낸 푸시가 없습니다."}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((r) => (
                                        <tr key={r.no} onClick={() => navigate("/notify/push")}>
                                            <td title={r.title}>{r.title}</td>
                                            <td css={numTd}>{r.isCollecting ? "집계 중" : fmt(r.messageCount)}</td>
                                            <td css={numTd}>{r.isCollecting ? "-" : fmt(r.readCount)}</td>
                                            <td
                                                css={[
                                                    numTd,
                                                    css`
                                                        color: ${r.readRate >= 1 ? GOOD : WEAK};
                                                        font-weight: 700;
                                                    `,
                                                ]}
                                            >
                                                {r.isCollecting ? "-" : `${r.readRate.toFixed(2)}%`}
                                            </td>
                                            <td>{fmtDate(r.sentAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div css={footRow}>
                        <span css={footNote}>
                            읽은 수는 푸시 또는 앱 알림함에서 내용을 연 회원을 알림별로 한 번씩 집계합니다. 배너 표시만으로는 집계하지 않으며, 과거 누락된 열람은 소급되지 않습니다.
                        </span>
                        <button type="button" css={moreBtn} onClick={() => navigate("/notify/push")}>
                            전체 목록 보기 &gt;
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default AppPushStatBox;

/* 카드 규격은 홈의 전체폭 카드와 같게 둔다 (ItemSaleStatsChart 기준) */
const cardStyle = css`
    width: 1220px;
    height: fit-content;
    min-height: 400px;
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1.5px solid ${Common.colors.gray200};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const headRow = css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const titleP = css`
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.black};
    font-size: 15px;
    margin: 0;
`;

const asOfSpan = css`
    font-size: 11px;
    color: ${Common.colors.gray500};
    font-variant-numeric: tabular-nums;
`;

const subP = css`
    font-size: 11.5px;
    color: ${Common.colors.gray600};
    margin: 2px 0 12px;
`;

const summaryRow = css`
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
`;

const summaryBox = css`
    flex: 1;
    padding: 10px 12px;
    border-radius: 6px;
    background: ${Common.colors.gray100};

    .label {
        margin: 0 0 4px;
        font-size: 11.5px;
        color: ${Common.colors.gray600};
    }

    .value {
        margin: 0;
        font-size: 20px;
        font-weight: 800;
        color: ${Common.colors.black};
        font-variant-numeric: tabular-nums;
    }
`;

const tableWrap = css`
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 2px;
    }
`;

const tableStyle = css`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;

    thead th {
        font-size: 10.5px;
        font-weight: 600;
        color: ${Common.colors.gray600};
        text-align: left;
        padding: 0 5px 6px;
        border-bottom: 1px solid ${Common.colors.gray200};
        white-space: nowrap;
    }

    tbody td {
        padding: 7px 5px;
        border-bottom: 1px solid ${Common.colors.gray100};
        color: ${Common.colors.gray700};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    tbody tr {
        cursor: pointer;
    }

    tbody tr:hover {
        background: ${Common.colors.gray100};
    }
`;

const rightTh = css`
    text-align: right !important;
`;

const numTd = css`
    text-align: right;
    font-variant-numeric: tabular-nums;
`;

const emptyTd = css`
    text-align: center;
    color: ${Common.colors.gray500};
    padding: 28px 0 !important;
`;

const messageStyle = css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Common.colors.gray500};
    font-size: 13px;
`;

const footRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
`;

const footNote = css`
    font-size: 11px;
    color: ${Common.colors.gray500};
`;

const moreBtn = css`
    border: 0;
    background: none;
    padding: 0;
    cursor: pointer;
    font-size: 11.5px;
    color: ${Common.colors.gray600};
`;
